import { useCallback } from "react"

import { type MetricValueFilterOperator as MetricFilterOperator } from "@/lib/metricValueFilters"

import type { RunResultsFilterController } from "./useRunResultsFilters"

interface UseRunResultsFilterActionsParams {
  addMetricFilters: RunResultsFilterController["addMetricFilters"]
  clearAllFilters: RunResultsFilterController["clearAllFilters"]
  clearMetricFilters: RunResultsFilterController["clearMetricFilters"]
  metricFilters: RunResultsFilterController["metricFilters"]
  mutationFilterIds: RunResultsFilterController["mutationFilterIds"]
  setMetricFilters: RunResultsFilterController["setMetricFilters"]
  setMutationFilterIds: RunResultsFilterController["setMutationFilterIds"]
  setStatusFilterStatuses: RunResultsFilterController["setStatusFilterStatuses"]
  statusFilterStatuses: RunResultsFilterController["statusFilterStatuses"]
}

export function useRunResultsFilterActions({
  addMetricFilters,
  clearAllFilters,
  clearMetricFilters,
  metricFilters,
  mutationFilterIds,
  setMetricFilters,
  setMutationFilterIds,
  setStatusFilterStatuses,
  statusFilterStatuses,
}: UseRunResultsFilterActionsParams) {
  const handleAddMetricFilter = useCallback(
    (
      metricName: string,
      operator: MetricFilterOperator,
      value: number | string | null,
      rangeMax?: number,
    ) => {
      const valueType = typeof value === "string" ? "string" : "number"
      addMetricFilters([
        {
          metricName,
          operator,
          value,
          rangeMax,
          valueType,
        },
      ])
    },
    [addMetricFilters],
  )

  const handleRemoveMetricFilter = useCallback(
    (index: number) => {
      const updated = metricFilters.filter((_, i) => i !== index)
      setMetricFilters(updated)
    },
    [metricFilters, setMetricFilters],
  )

  const handleClearAllMetricFilters = useCallback(() => {
    clearMetricFilters()
  }, [clearMetricFilters])

  const handleClearAllFilters = useCallback(() => {
    clearAllFilters()
  }, [clearAllFilters])

  const handleRemoveMutationFilter = useCallback(
    (mutationId: string) => {
      setMutationFilterIds(mutationFilterIds.filter((id) => id !== mutationId))
    },
    [mutationFilterIds, setMutationFilterIds],
  )

  const handleRemoveStatusFilter = useCallback(
    (status: string) => {
      setStatusFilterStatuses(statusFilterStatuses.filter((s) => s !== status))
    },
    [statusFilterStatuses, setStatusFilterStatuses],
  )

  return {
    handleAddMetricFilter,
    handleClearAllFilters,
    handleClearAllMetricFilters,
    handleRemoveMetricFilter,
    handleRemoveMutationFilter,
    handleRemoveStatusFilter,
  }
}
